<template>
	<Dialog
		:header="$t(locales.header)"
		v-model:visible="showDialog"
		:breakpoints="{'960px': '75vw', '640px': '100vw'}"
		:style="{width: '30vw'}"
		@hide="closeDialog"
		:modal="true"
	>
		<div class="p-grid" style="height: 19.125rem">
			<div v-if="options.length > 0" class="p-col-12">
				<p>{{$t(locales.notice.selectReason)}}</p>
				<Dropdown
					v-model="selectedOption"
					:options="options"
					optionLabel="label"
					:placeholder="$t(locales.selectPlaceholder)"
					style="width: 100%;"
					@change="onChangeOption($event)"
				/>
			</div>
			<div class="p-col-12 p-field p-input-filled">
				<p>{{$t(locales.notice.comment)}}</p>
				<InputText type="text"
							v-model="value.comment"
							style="width: 100%;"
							:disabled="!isCommentRequired && options.length > 0"
							:class="{'p-invalid':v$.comment.$invalid && confirmed}"
				/>
				<small v-if="(v$.comment.$invalid && confirmed) || v$.comment.$pending.$response" class="p-error">{{ $t('Squeeze.Validation.Dialogs.General.MissingComment') }}</small>
			</div>
		</div>
		<template #footer>
			<Button :label="$t(locales.buttons.abort)" @click="showDialog = false" icon="mdi mdi-close" class="p-button-text"/>
			<Button :label="$t(locales.buttons.confirm)" @click="confirm" icon="mdi mdi-check" autofocus />
		</template>
	</Dialog>
</template>

<script lang="ts">
import {computed, defineComponent, PropType, reactive, ref, watch} from 'vue';
import Dialog from "primevue/dialog";
import Dropdown from "primevue/dropdown";
import InputText from "primevue/inputtext";
import {Document} from "@dex/squeeze-client-ts";
import {useVuelidate} from "@vuelidate/core";
import {required} from "@vuelidate/validators";

interface Option {
	label: string;
	value: string;
	requiresComment?: boolean;
}

interface Locales {
	header: string;
	notice: {
		selectReason: string;
		comment: string;
	};
	selectPlaceholder: string;
	buttons: {
		confirm: string;
		abort: string;
	};
}

export default defineComponent({
	name: "DialogOptionsConfirm",
	components: {
		Dialog,
		Dropdown,
		InputText,
	},
	props: {
		show: {
			type: Boolean,
			default: false,
		},
		/** Reasons to choose from while deleting or suspending a document */
		options: {
			type: Array as PropType<Option[]>,
			default: () => [],
		},
		/** Localization for dialog text */
		locales: {
			type: Object as PropType<Locales>,
			required: true,
		},
		currentDocument: {
			type: Object as PropType<Document|null>,
			default: null,
		},
	},
	emits: ["onClose", "onConfirm"],
	setup(props, {emit}) {
		/** Comment for confirmation */
		const value = reactive<any>({
			comment: '',
		})

		/** Determines the required rules for validation */
		const rules = {
			comment: { required },
		}

		/** Use Vuelidate */
		const v$ = useVuelidate(rules, value);

		const confirmed = ref<boolean>(false);

		/** Indicates if the dialog is displayed */
		const showDialog = ref<boolean>(false);

		/** Selected option of options (dropdown) */
		const selectedOption = ref<Option>({
			label: "",
			value: "",
		});

		/** Should a comment be mandatory for selected option? */
		const isCommentRequired = computed(() => {
			if (props.options.length === 0) {
				return true;
			}

			if (selectedOption.value.requiresComment !== undefined) {
				return selectedOption.value.requiresComment;
			}

			return false;
		});

		/** Emits confirmation event with comment */
		const confirm = () => {
			confirmed.value = true;
			if (props.options.length > 0 && !isCommentRequired.value) {
				value.comment = selectedOption.value.label;
			}

			if (value.comment.length > 0) {
				selectedOption.value = { label: "", value: "" };
				emit("onConfirm", value.comment);
			}
		}

		/** Closes the dialog */
		const closeDialog = () => {
			showDialog.value = false;
			selectedOption.value = { label: "", value: "" };
			emit("onClose");
		}

		/**
		 * Triggered when the option is changed
		 * @param event
		 */
		const onChangeOption = (event: { originalEvent: PointerEvent; value: Option }) => {
			selectedOption.value = event.value;
		}

		watch(() => props.show, () => {
			// Save show in local variable, because show is not allowed to me mutated inside. React on change from show from parent
			showDialog.value = props.show;

			// check if current document has an error text and if status is suspend
			if (props.currentDocument?.workflowContext?.errorText && props.currentDocument?.workflowContext?.status === "SUSPEND") {
				value.comment = props.currentDocument.workflowContext.errorText;
			} else {
				value.comment = "";
				confirmed.value = false;
			}
		})

		return {
			v$,
			value,
			rules,
			confirmed,
			showDialog,
			selectedOption,
			confirm,
			closeDialog,
			onChangeOption,
			isCommentRequired,
		}
	},
});

</script>

<style scoped>

</style>
